// The character-select / creation / My Account screen. Sits between sign-in and
// the world: sign-in (ui.ts) hands us a session token; here the player picks a
// saved hero (or creates one, up to the per-account cap) and enters. It also
// hosts a basic "My Account" page (email + verification, change password, delete
// account). Reachable again in-world via the "Switch character" control.
//
// This is a procedural DOM module (the settings.ts / weatherdev.ts pattern),
// driving the #charselect markup in index.html. The pre-world data all comes
// over the token-authenticated REST API (see src/server/http-api.ts); the WS
// join happens only once the player enters, via game.ts's onEnter callback.
//
// The select view's rotating 3D hero is the game's real buildPlayer rig in a
// small standalone scene. The CREATE view is the shared character designer
// (src/chardesign.ts) — the same module the studio's Character view mounts —
// which brings its own preview + tunables.

import * as THREE from "three";
import { $ } from "./dom.ts";
import { buildPlayer } from "./player.ts";
import { disposeGroup } from "./meshkit.ts";
import { FOV } from "./config.ts";
import { makeCharDesigner, type CharDesigner } from "./chardesign.ts";
import { makeGraphicsControls } from "./settings.ts";
import { makeAudioControls } from "./audio.ts";
import { DEFAULT_APPEARANCE, MAX_CHARACTERS, type Appearance, type CharacterSummary } from "./shared.ts";

type CharSelectHandlers = {
  /** Enter the world as the given character (game.ts opens the WS with it). */
  onEnter: (characterId: string) => void;
  /** Sign out entirely — clear the token + return to the sign-in screen. */
  onLogout: () => void;
};

let token = "";
let characters: CharacterSummary[] = [];
let selectedId: string | null = null;
let maxChars = MAX_CHARACTERS;
let account: { email: string; hasPassword: boolean } | null = null;
let handlers: CharSelectHandlers = { onEnter: () => {}, onLogout: () => {} };
let wired = false;

// ---- token-authenticated REST ---------------------------------------------

async function authFetch(path: string, opts: RequestInit = {}): Promise<any> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...(opts.headers as Record<string, string> | undefined),
  };
  if (opts.body) headers["Content-Type"] = "application/json";
  const res = await fetch(path, { ...opts, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || "something went wrong");
  return data;
}

const esc = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
const hex = (c: number) => "#" + (c >>> 0 & 0xffffff).toString(16).padStart(6, "0");

/** "just now" / "3h ago" / "2d ago" — a relative timestamp for the card. */
function ago(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 90) return "just now";
  const m = s / 60;
  if (m < 90) return `${Math.round(m)}m ago`;
  const h = m / 60;
  if (h < 36) return `${Math.round(h)}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

// ---- the 3D hero preview (one shared WebGL context) ------------------------

let preview: {
  setLook: (a: Appearance) => void;
  resize: () => void;
  start: () => void;
  stop: () => void;
} | null = null;

function ensurePreview() {
  if (preview) return preview;
  const canvas = $<HTMLCanvasElement>("#csCanvas");
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(FOV, 1, 0.1, 100);
  camera.position.set(0, 1.05, 3.2);
  camera.lookAt(0, 0.98, 0);
  // Noon-ish toon lighting, mirroring daynight so the rig reads like in-world.
  scene.add(new THREE.HemisphereLight(0xbcd7ff, 0x4a5a3a, 0.75));
  const dir = new THREE.DirectionalLight(0xffffff, 1.8);
  dir.position.set(2.4, 4, 3);
  scene.add(dir);
  scene.add(new THREE.AmbientLight(0xffffff, 0.28));

  let rig: THREE.Group | null = null;
  let raf = 0;
  let last = 0;

  function setLook(a: Appearance) {
    // Dispose the old rig's per-instance geometries (buildPlayer mints fresh
    // cones/cylinders per limb); disposeGroup keeps the shared box/toon caches.
    if (rig) { scene.remove(rig); disposeGroup(rig); }
    rig = buildPlayer(a);
    rig.rotation.y = 0.35; // start on a slight 3/4 angle, not dead-on
    scene.add(rig);
  }
  function resize() {
    const r = canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(r.width));
    const h = Math.max(1, Math.round(r.height));
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  function frame(t: number) {
    raf = requestAnimationFrame(frame);
    const dt = last ? Math.min(0.05, (t - last) / 1000) : 0;
    last = t;
    if (rig) rig.rotation.y += dt * 0.55;
    renderer.render(scene, camera);
  }
  preview = {
    setLook,
    resize,
    start() { if (!raf) { last = 0; resize(); raf = requestAnimationFrame(frame); } },
    stop() { if (raf) { cancelAnimationFrame(raf); raf = 0; } },
  };
  return preview;
}

// ---- views -----------------------------------------------------------------

function view(v: "select" | "create" | "account" | "settings") {
  $("#csSelect").classList.toggle("hide", v !== "select");
  $("#csCreate").classList.toggle("hide", v !== "create");
  $("#csAccountView").classList.toggle("hide", v !== "account");
  $("#csSettingsView").classList.toggle("hide", v !== "settings");
  // Create is a FULL-SCREEN experience (the designer paints the whole scene) —
  // the night-slate panel steps aside for it.
  $("#charselect .cspanel").classList.toggle("hide", v === "create");
  // The saved-hero 3D preview lives in the select view (the stage starts as a
  // panel-level div — parent it in on first show); the create view's preview
  // belongs to the shared designer module. Pause whichever is hidden.
  if (v === "select") { $("#csSelectStage").appendChild($("#csStage")); preview?.start(); preview?.resize(); designer?.stop(); }
  else if (v === "create") { preview?.stop(); designer?.start(); designer?.resize(); }
  else { preview?.stop(); designer?.stop(); }
  $("#csConfirm").classList.add("hide");
}

function renderCards() {
  const list = $("#csList");
  list.innerHTML = "";
  for (const c of characters) {
    const card = document.createElement("div");
    card.className = "cscard" + (c.id === selectedId ? " sel" : "");
    card.dataset.id = c.id;
    const chips = [c.look.shirt, c.look.skin, c.look.hair]
      .map((col) => `<span class="cschip" style="background:${hex(col)}"></span>`)
      .join("");
    const meta = [`⚔ ${c.kills}`, ago(c.updatedAt)].filter(Boolean).join(" · ");
    card.innerHTML =
      `<div class="cschips">${chips}</div>` +
      `<div class="cscardmain">` +
        `<div class="cscardname">${esc(c.name)}${c.title ? `<span class="csrank">${esc(c.title)}</span>` : ""}</div>` +
        `<div class="cscardmeta">${meta}</div>` +
      `</div>` +
      `<button class="csdel cssecondary" type="button" title="Delete hero" aria-label="Delete hero">✕</button>`;
    card.onclick = () => select(c.id);
    (card.querySelector(".csdel") as HTMLButtonElement).onclick = (e) => {
      e.stopPropagation();
      confirmDelete(c);
    };
    list.appendChild(card);
  }
  if (characters.length < maxChars) {
    const add = document.createElement("div");
    add.className = "cscard cscardnew";
    add.textContent = "＋ New hero";
    add.onclick = openCreate;
    list.appendChild(add);
  }
}

function select(id: string) {
  selectedId = id;
  for (const el of $("#csList").querySelectorAll<HTMLElement>(".cscard"))
    el.classList.toggle("sel", el.dataset.id === id);
  const c = characters.find((x) => x.id === id);
  const nameEl = $("#csPreviewName");
  if (c) {
    preview?.setLook(c.look);
    nameEl.innerHTML = `${esc(c.name)}${c.title ? `<span class="cstitleline">${esc(c.title)}</span>` : ""}`;
  }
  ($("#csEnter") as HTMLButtonElement).disabled = !c;
}

async function refreshList() {
  const errEl = $("#csSelectErr");
  errEl.textContent = "";
  try {
    const data = await authFetch("/api/characters");
    characters = (data.characters ?? []) as CharacterSummary[];
    maxChars = data.max ?? MAX_CHARACTERS;
  } catch (e: any) {
    view("select");
    errEl.textContent = e.message;
    return;
  }
  if (characters.length === 0) { openCreate(); return; }
  if (!selectedId || !characters.some((c) => c.id === selectedId)) selectedId = characters[0]!.id;
  renderCards();
  select(selectedId);
  view("select");
}

// ---- create ----------------------------------------------------------------
// The create view's whole body is the shared character designer
// (src/chardesign.ts) — the SAME module the studio's Character view mounts, so
// the page iterated there is exactly what players see here.

let designer: CharDesigner | null = null;

function ensureDesigner(): CharDesigner {
  if (designer) return designer;
  designer = makeCharDesigner({
    actions: {
      primary: { label: "Create & play", onClick: () => void doCreate() },
      secondary: {
        label: "Back",
        onClick: () => {
          if (characters.length) view("select");
          else { hideCharSelect(); handlers.onLogout(); }
        },
      },
    },
  });
  $("#csCreate").appendChild(designer.el);
  return designer;
}

function openCreate() {
  const d = ensureDesigner();
  d.setLook({ ...DEFAULT_APPEARANCE });
  d.setName("");
  d.setError("");
  d.setBusy(false);
  // No characters yet → nothing to go "back" to; offer Log out instead.
  d.setSecondaryLabel(characters.length ? "Back" : "Log out");
  view("create");
  d.focusName();
}

async function doCreate() {
  const d = ensureDesigner();
  const name = d.getName();
  if (!name) { d.setError("name your hero"); return; }
  d.setBusy(true);
  d.setError("");
  try {
    const data = await authFetch("/api/characters", {
      method: "POST",
      body: JSON.stringify({ name, look: d.getLook() }),
    });
    enter(data.id); // straight into the world with the hero you just made
  } catch (e: any) {
    d.setError(e.message);
    d.setBusy(false);
  }
}

// ---- Settings (graphics quality + audio) -----------------------------------
// The controls are the SAME builders the in-world O-panel uses, so a choice
// made here shares the persisted state and applies once the world loads. The
// graphics row self-syncs via onQualityChange; the audio sliders refresh on
// each open so a volume tweaked in-world shows through when we come back.

let audioControls: { el: HTMLElement; refresh: () => void } | null = null;

function ensureSettings() {
  if (audioControls) return;
  $("#csGfxMount").appendChild(makeGraphicsControls());
  audioControls = makeAudioControls();
  $("#csAudioMount").appendChild(audioControls.el);
}

function openSettings() {
  ensureSettings();
  audioControls?.refresh();
  view("settings");
}

// ---- My Account ------------------------------------------------------------

async function openAccount() {
  view("account");
  $("#csEmail").textContent = "…";
  $("#csVerified").textContent = "";
  $("#csAccountErr").textContent = "";
  setNote("#csPwNote", "");
  setNote("#csResendNote", "");
  ($("#csCurrent") as HTMLInputElement).value = "";
  ($("#csNewPw") as HTMLInputElement).value = "";
  try {
    const a = await authFetch("/api/account");
    account = { email: a.email, hasPassword: !!a.hasPassword };
    $("#csEmail").textContent = a.email;
    if (a.emailVerified) {
      $("#csVerified").innerHTML = '<span class="csbadge-ok">✓ Verified</span>';
      $("#csResend").classList.add("hide");
    } else {
      $("#csVerified").innerHTML = '<span class="csbadge-no">Not verified</span>';
      $("#csResend").classList.remove("hide");
    }
    // A Google-only account has no current password to confirm.
    $("#csCurrent").classList.toggle("hide", !a.hasPassword);
  } catch (e: any) {
    $("#csAccountErr").textContent = e.message;
  }
}

function setNote(sel: string, msg: string, ok = true) {
  const el = $(sel);
  el.textContent = msg;
  el.style.color = ok ? "var(--wl-ok)" : "var(--wl-danger)";
}

// ---- typed-confirmation dialog (delete hero / delete account) --------------

function confirmDialog(opts: {
  message: string;
  word: string;
  needPassword?: boolean;
  confirmLabel?: string;
  onConfirm: (pw: string) => Promise<void>;
}) {
  const box = $("#csConfirm");
  const input = $<HTMLInputElement>("#csConfirmInput");
  const pw = $<HTMLInputElement>("#csConfirmPw");
  const yes = $<HTMLButtonElement>("#csConfirmYes");
  const no = $<HTMLButtonElement>("#csConfirmNo");
  const err = $("#csConfirmErr");
  $("#csConfirmMsg").innerHTML = opts.message;
  input.value = "";
  input.placeholder = `type ${opts.word} to confirm`;
  pw.value = "";
  pw.classList.toggle("hide", !opts.needPassword);
  err.textContent = "";
  yes.textContent = opts.confirmLabel ?? "Confirm";
  const check = () => {
    yes.disabled = !(input.value === opts.word && (!opts.needPassword || pw.value.length > 0));
  };
  yes.disabled = true;
  input.oninput = check;
  pw.oninput = check;
  no.onclick = () => box.classList.add("hide");
  yes.onclick = async () => {
    yes.disabled = true;
    err.textContent = "";
    try {
      await opts.onConfirm(pw.value);
      box.classList.add("hide");
    } catch (e: any) {
      err.textContent = e.message;
      check();
    }
  };
  box.classList.remove("hide");
  input.focus();
}

function confirmDelete(c: CharacterSummary) {
  confirmDialog({
    message: `Type <b>${esc(c.name)}</b> to permanently delete this hero and everything it owns.`,
    word: c.name,
    confirmLabel: "Delete forever",
    onConfirm: async () => {
      await authFetch(`/api/characters/${encodeURIComponent(c.id)}`, { method: "DELETE" });
      if (selectedId === c.id) selectedId = null;
      await refreshList();
    },
  });
}

// ---- enter / hide ----------------------------------------------------------

function enter(characterId: string) {
  hideCharSelect();
  handlers.onEnter(characterId);
}

// ---- one-time wiring -------------------------------------------------------

function wire() {
  if (wired) return;
  wired = true;

  ($("#csEnter") as HTMLButtonElement).onclick = () => { if (selectedId) enter(selectedId); };
  $("#csAccount").onclick = () => void openAccount();
  $("#csSettings").onclick = () => openSettings();
  $("#csLogout").onclick = () => { hideCharSelect(); handlers.onLogout(); };

  // (The create view's buttons + name field belong to the shared designer —
  // wired in ensureDesigner.)

  $("#csAccountBack").onclick = () => view("select");
  $("#csSettingsBack").onclick = () => view("select");
  $("#csResend").onclick = async () => {
    setNote("#csResendNote", "sending…");
    try {
      const r = await authFetch("/api/account/resend-verify", { method: "POST" });
      setNote("#csResendNote", r.sent ? "Sent — check your inbox." : "Already verified.");
    } catch (e: any) {
      setNote("#csResendNote", e.message, false);
    }
  };
  $("#csSavePw").onclick = async () => {
    const cur = ($("#csCurrent") as HTMLInputElement).value;
    const next = ($("#csNewPw") as HTMLInputElement).value;
    if (next.length < 8) { setNote("#csPwNote", "password must be at least 8 characters", false); return; }
    setNote("#csPwNote", "saving…");
    try {
      await authFetch("/api/account/password", { method: "POST", body: JSON.stringify({ current: cur, password: next }) });
      ($("#csCurrent") as HTMLInputElement).value = "";
      ($("#csNewPw") as HTMLInputElement).value = "";
      setNote("#csPwNote", "Password updated.");
    } catch (e: any) {
      setNote("#csPwNote", e.message, false);
    }
  };
  $("#csDeleteAcct").onclick = () => {
    if (!account) return;
    confirmDialog({
      message: `Type your email <b>${esc(account.email)}</b> to permanently delete your account and <b>all</b> heroes. This cannot be undone.`,
      word: account.email,
      needPassword: account.hasPassword,
      confirmLabel: "Delete account",
      onConfirm: async (pw) => {
        await authFetch("/api/account", { method: "DELETE", body: JSON.stringify({ password: pw }) });
        hideCharSelect();
        handlers.onLogout();
      },
    });
  };

  // Enter submits per field; every keydown stops propagation so typing never
  // leaks into the in-world game handlers (this screen reopens while joined, via
  // Switch character).
  const onEnter = (sel: string, run: () => void) =>
    $(sel).addEventListener("keydown", (e) => {
      e.stopPropagation();
      if ((e as KeyboardEvent).key === "Enter") run();
    });
  const confirmIfReady = () => { const y = $<HTMLButtonElement>("#csConfirmYes"); if (!y.disabled) y.click(); };
  onEnter("#csCurrent", () => $("#csSavePw").click());
  onEnter("#csNewPw", () => $("#csSavePw").click());
  onEnter("#csConfirmInput", confirmIfReady);
  onEnter("#csConfirmPw", confirmIfReady);

  window.addEventListener("resize", () => {
    if ($("#charselect").classList.contains("show")) preview?.resize();
  });
}

// ---- public API ------------------------------------------------------------

/** Wire the screen once (call at boot before any show). */
export function initCharSelect(h: CharSelectHandlers): void {
  handlers = h;
  wire();
}

/** Show the character-select screen for a freshly-authenticated session token,
 *  and load the account's heroes. Reused after auth, on saved-token return, on a
 *  `needCharacter` bounce, and from the in-world "Switch character" control. */
export function showCharSelect(sessionToken: string): void {
  token = sessionToken;
  wire();
  $("#charselect").classList.add("show");
  ensurePreview().start();
  view("select");
  $("#csList").innerHTML = '<div class="cscardmeta" style="padding:10px">Loading heroes…</div>';
  void refreshList();
}

/** Hide the screen and pause both preview render loops. */
export function hideCharSelect(): void {
  $("#charselect").classList.remove("show");
  preview?.stop();
  designer?.stop();
}
